import type {DocumentNode} from "graphql";
import gql from "graphql-tag";

import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {
    archiveContentAssignmentsType,
    getClassSelectorType,
    getCoachedClassroomsType,
    getStudentsType,
    simpleCompletionQueryType,
} from "./__graphql-types__/index.ts";

export const ASSIGNMENT_FRAGMENT: DocumentNode = gql`
    fragment AssignmentInfoFragment on Assignment {
        id
        contents {
            id
            title: translatedTitle
            kind
        }
        studentList {
            id
            cacheId
            name
            isK4dClassroom
            isChildAssignmentsClassroom
            signupCode
        }
        students {
            id
            kaid
        }
        startDate
        dueDate
        isDraft
        subjectSlug
        title
        instructions
    }
`;

export const ARCHIVE_ASSIGNMENTS_MUTATION: GqlOperation<
    archiveContentAssignmentsType["response"],
    archiveContentAssignmentsType["variables"]
> = gqlOp(gql`
    mutation archiveContentAssignments($assignmentIds: [ID]!) {
        archiveAssignments(ids: $assignmentIds) {
            assignments {
                ...AssignmentInfoFragment
            }
        }
    }
    ${ASSIGNMENT_FRAGMENT}
`);

// Exported for testing.
export const CLASS_SELECTOR_QUERY: GqlOperation<
    getClassSelectorType["response"],
    getClassSelectorType["variables"]
> = gqlOp(gql`
    query getClassSelector {
        coach: user {
            id
            studentLists: coachedStudentLists {
                id
                descriptor
                cacheId
                name
                signupCode
                countStudents
                hasKADAiGuideEnrolledStudents
                isK4dClassroom
            }
        }
    }
`);

export const STUDENT_SELECTOR_QUERY: GqlOperation<
    getStudentsType["response"],
    getStudentsType["variables"]
> = gqlOp(gql`
    query getStudents(
        $classDescriptor: String!
        $aiGuideEnabledStudentsOnly: Boolean!
    ) {
        classroom: classroomByDescriptor(descriptor: $classDescriptor) {
            id
            cacheId
            studentKaidsAndNicknames {
                id
                kaid
                coachNickname
            }
            students @include(if: $aiGuideEnabledStudentsOnly) {
                id
                kaid
                hasAccessToAIGuideLearner
                isAIGuideEnabled
            }
        }
    }
`);

export const USE_GET_COACHED_CLASSROOMS: GqlOperation<
    getCoachedClassroomsType["response"],
    getCoachedClassroomsType["variables"]
> = gqlOp(gql`
    query getCoachedClassrooms {
        user {
            id
            coachedClassrooms(shouldOmitCACs: true) {
                id
                cacheId
                name
                descriptor
                students: studentKaidsAndNicknames {
                    id
                    nickname: coachNickname
                }
                countStudents
            }
        }
    }
`);

// This query is deliberately a superset of ASSIGNMENT_CSV_DATA_FRAGMENT
// from assignment-csv.js; therefore downloading the csv doesn't require an
// additional graphql operation. The few fields not directly used in this
// file are cheap to fetch and small to send down, so this is fine w/rt perf.
export const SIMPLE_COMPLETION_QUERY: GqlOperation<
    simpleCompletionQueryType["response"],
    simpleCompletionQueryType["variables"]
> = gqlOp(gql`
    query simpleCompletionQuery($assignmentId: String!, $teacherKaid: String) {
        coach: user {
            id
            assignment: assignmentByThisUser(id: $assignmentId) {
                contents {
                    id
                    translatedTitle
                    defaultUrlPath
                    kind
                }
                assignedDate
                dueDate
                id
                itemCompletionStates: itemCompletionStatesForAllStudents {
                    student {
                        id
                        kaid
                        coachNickname(teacherKaid: $teacherKaid)
                        profileRoot
                    }
                    state
                    completedOn
                    bestScore {
                        numCorrect
                        numAttempted
                    }
                    bestScoreSkills {
                        numLeveledUp
                        numLeveledDown
                        levelChanges {
                            id
                            before
                            after
                            changeDirection
                            exercise {
                                id
                                translatedTitle
                            }
                        }
                    }
                    exerciseAttempts {
                        id
                        isCompleted
                        isRestarted
                        numAttempted
                        numCorrect
                        lastAttemptDate
                    }
                    activitySubmissions {
                        threadID
                        thread {
                            id
                            interactions {
                                id
                                answer
                                question
                            }
                        }
                    }
                }
                studentList {
                    id
                    cacheId
                    name
                }
            }
        }
    }
`);
